import React from 'react';

import { useRouteMatch, Switch, Route, useHistory } from 'react-router-dom';
import { useUserWithRole } from 'hooks/custom-auth';
import { useStagesWithStatus } from 'hooks/profile';
import { useUserOrganization } from 'hooks/organization';
import { useDocument } from 'react-firebase-hooks/firestore';
import { getRoutePrefix } from 'utils/routes';
import {
  setStagesPath,
  isProfileCompleteWithStatus,
  setStagesStatus,
} from 'utils/profile-stages';

import Client from 'containers/app/client';
import WorkerStages from 'containers/worker/stages';
import EmployerStages from 'containers/employer/stages';

import { LayoutPage, LayoutHorizontal } from 'components/layouts';
import LoadingFullPage from 'components/loading-full-page';
import UserStories from 'components/user-stories';
import { TITLE as OrgSizeTitle } from 'containers/employer/profile-stages/organization-size/constants';

import RedirectToFirst from './redirect-to-first';
import Sidebar from './sidebar';

import './style.css';

const getStagesByRole = (role) => {
  if (role == null) return null;
  if (role === 'isWorker') return WorkerStages;
  if (role === 'isEmployer') return EmployerStages;
  return null;
};

const ProfileStagesPage = () => {
  const history = useHistory();
  const match = useRouteMatch();
  const { params } = useRouteMatch(`${match.path}/:stage`) || {};
  const [user, loading, error] = useUserWithRole(Client.auth);
  const [organization, loadingOrganization, errorOrganization] = useUserOrganization();
  const [stagesByRole, loadingStages] = useStagesWithStatus(user && getStagesByRole(user.claims.role));
  const [profile, profileLoading, profileError] = useDocument(Client.dbProfile());

  if (loading || loadingStages || loadingOrganization || profileLoading)return <LoadingFullPage />;
  if (error) throw error;
  if (errorOrganization) throw errorOrganization;
  if (profileError) throw profileError;

  let stagesFiltered = stagesByRole;

  if (organization) {
    if (organization.id && organization.admins && organization.admins.users) {
      const admin = organization.admins.users.find(
        (item) => item.email === user.email
      );
      if (admin) {
        stagesFiltered = stagesFiltered.filter(
          (stage) => stage.title !== OrgSizeTitle
        );
      }
    }
  }

  const { isProfileComplete } = profile.data() || {};
  const routePrefix = getRoutePrefix(user.claims.role);
  const stages = setStagesPath(stagesFiltered);

  const currentStep =
    stages.findIndex(({ path: stagePath }) => {
      if (params == null) return 1;
      return stagePath === params.stage;
    }) || 0;

  const onComplete = () => {
    const nextStep = currentStep + 1;
    const finish = !stages[nextStep] || stages[nextStep].disabled;
    if (finish) {
      if (isProfileComplete !== true) history.push(`/profile-complete`);
      else history.push(`${getRoutePrefix(user.claims.role)}/dashboard`);
    } else {
      history.push(`${match.url}/${stages[currentStep + 1].path}`);
    }
  };

  const onSubmit = async (vals, actions, handleValues) => {
    try {
      await handleValues(vals);

      const stagesWithStatus = await setStagesStatus(stages);
      const isProfileCompleteNow = isProfileCompleteWithStatus(stagesWithStatus) === stagesWithStatus.length;
      await Client.setUserProfileComplete(isProfileCompleteNow);
      actions.setSubmitting(false);
      onComplete();
    } catch (e) {
      console.error(e.title, e.errors);
      actions.setSubmitting(false);
    }
  };

  const onBack = async (vals, handleValues) => {
    try {
      console.info('saved', vals);
      await handleValues(vals);
      history.push(`${routePrefix}/dashboard`);
    } catch (e) {
      console.error(e.title, e.errors);
    }
  };

  return (
    <LayoutPage className="complete-profile-page" header={false} footer={false}>
      <LayoutHorizontal className="complete-profile-page-container">
        <Sidebar stages={stages} currentStep={currentStep} />
        <LayoutPage.Container className="complete-profile-form">
          <div className="wrapper">
            <Switch>
              <Route path={match.path} exact>
                <RedirectToFirst stages={stages} />
              </Route>
              {stages
                .filter(({ disabled }) => !disabled)
                .map(
                  ({ title, component: Component, path: stagePath }, i) => {
                    const path = `${match.path}/${stagePath}`;
                    return (
                      <Route key={i} path={path}>
                        <h1>{title}</h1>
                        <Component
                          onSubmit={onSubmit}
                          user={user}
                          onBack={onBack}
                        />
                      </Route>
                    );
                  }
                )}
            </Switch>
            <UserStories />
          </div>
        </LayoutPage.Container>
      </LayoutHorizontal>
    </LayoutPage>
  );
};

ProfileStagesPage.propTypes = {};

export default ProfileStagesPage;
